import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { BackButton } from '../components/BackButton';
import { ErrorMessage } from '../components/LoadingGrid';
import { VideoPlayer } from '../components/VideoPlayer';
import { api } from '../lib/api-client';
import { chapterIdForUrl } from '../lib/chapter-id';
import { useCurrentUser } from '../lib/hooks';
import { useLang } from '../lib/lang';
import type { StreamSource, VideoInfo } from '../lib/types';

/** Pilihan kecepatan pemutaran yang ditawarkan ke user. */
const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/** Skor resolusi (720p → 720). Dipakai untuk urutan & pilihan terbaik. */
function score(r: string): number {
  const m = /(\d{3,4})/.exec(r.toLowerCase());
  return m === null || m[1] === undefined ? 0 : Number.parseInt(m[1], 10);
}

/** Best stream = resolusi tertinggi (mirror preferredStream di backend). */
function pickBestStream(streams: StreamSource[]): StreamSource | null {
  if (streams.length === 0) return null;
  return [...streams].sort((a, b) => score(b.resolution) - score(a.resolution))[0] ?? null;
}

/**
 * Label `resolution` dari upstream sangat tidak seragam antar platform:
 *   dramabox  → "1080p", "720p"        (rapi)
 *   wetv      → "1080P", "360P"        (huruf P besar)
 *   reelshort → "720p", "540p", "multi"
 *   happyshort→ "default"
 *   rapidtv   → "auto", "auto", "auto", "auto"
 *
 * Hanya label yang memuat ANGKA yang benar-benar menawarkan pilihan ke user;
 * "auto"/"multi"/"default" tidak bisa dibedakan satu sama lain.
 */
function isRealQuality(resolution: string): boolean {
  return /\d{3,4}/.test(resolution);
}

/** Samakan penulisan supaya "1080P" dan "1080p" tidak jadi dua tombol. */
function qualityLabel(resolution: string): string {
  return resolution.trim().replace(/p$/i, 'p');
}

export function PlayerPage() {
  const { platform, id, chapterId } = useParams<{
    platform: string;
    id: string;
    chapterId: string;
  }>();
  const lang = useLang();
  const navigate = useNavigate();

  // Chapter aktif disimpan sebagai STATE (bukan route param) supaya ganti
  // episode TIDAK me-navigate/re-mount — persis pendekatan single-<video>
  // di referensi. Ini yang membuat FULLSCREEN bertahan saat auto-next di
  // iOS/Android (element video tidak pernah unmount). URL di-sync via
  // replaceState untuk shareability.
  const [cid, setCid] = useState(chapterId ?? '');

  // Kualitas yang dipilih user (label resolusi). null = otomatis/terbaik.
  const [quality, setQuality] = useState<string | null>(null);
  // Kecepatan pemutaran; bertahan saat ganti episode dalam sesi ini.
  const [rate, setRate] = useState(1);

  const videoQuery = useQuery({
    queryKey: ['video', platform, id, cid, lang],
    queryFn: () => api.video(platform!, id!, cid, lang),
    enabled: Boolean(platform) && Boolean(id) && cid.length > 0,
    placeholderData: keepPreviousData,
  });

  // Daftar episode untuk strip pemilih — biasanya sudah cached dari DetailPage.
  const detailQuery = useQuery({
    queryKey: ['detail', platform, id, lang],
    queryFn: () => api.detail(platform!, id!, lang),
    enabled: Boolean(platform) && Boolean(id),
  });

  const meQuery = useCurrentUser();
  const loggedIn = (meQuery.data?.user ?? null) !== null;
  const qc = useQueryClient();
  const recordedRef = useRef<string>('');
  const data = videoQuery.data?.data;

  // ─── Lanjutkan menonton ───
  // Riwayat dipakai dua arah: membaca posisi terakhir episode ini, dan
  // menyimpan posisi terbaru selama menonton.
  const historyQuery = useQuery({
    queryKey: ['history'],
    queryFn: () => api.history(),
    enabled: loggedIn,
  });

  const savedEntry = (historyQuery.data?.items ?? []).find(
    (h) =>
      h.platform === platform &&
      h.externalDramaId === id &&
      h.externalEpisodeId === cid,
  );
  // Episode yang sudah tamat mulai dari awal lagi, bukan di detik terakhir.
  const startAt =
    savedEntry && !savedEntry.completed ? savedEntry.positionSeconds : 0;

  // Player baru dirender setelah riwayat diketahui, supaya seek "lanjutkan"
  // tidak keburu terlewat. Biasanya instan karena beranda sudah memuatnya.
  const historyReady =
    !loggedIn || historyQuery.isSuccess || historyQuery.isError;

  // Posisi terakhir yang diketahui + kapan terakhir dikirim ke server.
  const posRef = useRef({ seconds: 0, duration: 0 });
  const lastSentAtRef = useRef(0);

  const saveRef = useRef<(force?: boolean, completed?: boolean) => void>(
    () => {},
  );
  saveRef.current = (force = false, completed = false) => {
    if (!loggedIn || !data || !platform || !id) return;
    const { seconds, duration } = posRef.current;
    if (seconds <= 0 && !completed) return;
    const now = Date.now();
    // Hemat kuota: kirim tiap 15 detik, kecuali dipaksa (pindah episode,
    // halaman ditutup, episode selesai).
    if (!force && now - lastSentAtRef.current < 15_000) return;
    lastSentAtRef.current = now;

    const detail = qc.getQueryData<{
      data: { title?: string; cover?: string | null };
    }>(['detail', platform, id, lang]);

    void api
      .recordHistory({
        platform,
        dramaId: id,
        episodeId: cid,
        episodeNumber: data.episodeIndex > 0 ? data.episodeIndex : 1,
        dramaTitle:
          detail?.data?.title || data.title || `Episode ${data.episodeIndex}`,
        ...(detail?.data?.cover ? { dramaPoster: detail.data.cover } : {}),
        positionSeconds: Math.floor(seconds),
        ...(duration > 0 ? { durationSeconds: Math.floor(duration) } : {}),
        completed:
          completed || (duration > 0 && seconds >= duration - 15),
      })
      .catch(() => {
        // best-effort — jangan ganggu playback.
      });
  };

  const handleProgress = (seconds: number, duration: number) => {
    posRef.current = { seconds, duration };
    saveRef.current(false);
  };

  // Simpan sekali lagi saat halaman ditutup / komponen dilepas, supaya
  // detik-detik terakhir tidak hilang.
  useEffect(() => {
    const flush = () => saveRef.current(true);
    // Hanya saat halaman DISEMBUNYIKAN; kalau tidak, event ini ikut menyala
    // ketika halaman kembali terlihat dan menghasilkan tulis sia-sia.
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush();
    };
    window.addEventListener('pagehide', flush);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flush);
      document.removeEventListener('visibilitychange', onVisibility);
      flush();
      void qc.invalidateQueries({ queryKey: ['history'] });
    };
  }, [qc]);

  // Catat riwayat begitu episode dimuat, sekali per (drama, episode).
  useEffect(() => {
    if (!loggedIn || !data || !platform || !id) return;
    const key = `${id}:${cid}`;
    if (recordedRef.current === key) return;
    recordedRef.current = key;
    // Episode berganti → mulai hitung posisi dari titik lanjut, JANGAN 0,
    // supaya entri yang sudah ada tidak ter-reset ke awal.
    posRef.current = { seconds: startAt, duration: 0 };
    lastSentAtRef.current = Date.now();
    // Poster + judul drama tidak ada di response /video — ambil dari cache
    // detail (user datang dari DetailPage) supaya Riwayat punya gambar.
    const detail = qc.getQueryData<{ data: { title?: string; cover?: string | null } }>(
      ['detail', platform, id, lang],
    );
    const poster = detail?.data?.cover ?? null;
    const dramaTitle =
      detail?.data?.title || data.title || `Episode ${data.episodeIndex}`;
    void api
      .recordHistory({
        platform,
        dramaId: id,
        episodeId: cid,
        episodeNumber: data.episodeIndex > 0 ? data.episodeIndex : 1,
        dramaTitle,
        ...(poster ? { dramaPoster: poster } : {}),
        positionSeconds: Math.floor(startAt),
        ...(data.durationSeconds !== null
          ? { durationSeconds: data.durationSeconds }
          : {}),
      })
      .catch(() => {
        // riwayat best-effort — jangan ganggu playback kalau gagal.
      });
  }, [loggedIn, data, platform, id, cid, lang, qc, startAt]);

  const goTo = (nextCid: string, completed = false) => {
    if (nextCid.length > 0 && nextCid !== '0') {
      // Simpan dulu posisi episode yang ditinggalkan; `completed` true bila
      // pindah karena episode habis (auto-next).
      saveRef.current(true, completed);
      setCid(nextCid);
      // Sinkron URL tanpa reload/re-mount (shareable + tombol back benar).
      // PENTING: pertahankan `window.history.state` yang ada (berisi indeks
      // navigasi React Router `idx`). Kalau di-set `null`, jejak history RR
      // hilang → tombol back malah lompat ke home.
      try {
        window.history.replaceState(
          window.history.state,
          '',
          `/player/${platform}/${id}/${nextCid}`,
        );
      } catch {
        // abaikan — navigasi in-app tetap jalan tanpa update URL.
      }
    }
  };

  const chapters = detailQuery.data?.data.chapters ?? [];
  const dramaTitle = detailQuery.data?.data.title ?? '';

  return (
    <div className="min-h-screen bg-ink-950">
      {/* Top bar */}
      <header className="flex items-center justify-between px-1 safe-top">
        {/* Lebar tetap: di Telegram BackButton tidak menggambar apa pun,
            wrapper ini menjaga nomor episode tetap di tengah. */}
        <div className="h-11 w-11">
          <BackButton />
        </div>
        {data && (
          <span className="text-xs font-semibold text-neutral-400">
            Ep {data.episodeIndex} / {data.totalEpisodes}
          </span>
        )}
        <button
          type="button"
          onClick={() => navigate(`/drama/${platform}/${id}`)}
          aria-label="Detail judul"
          className="grid h-11 w-11 place-items-center"
        >
          <svg className="text-neutral-300" width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
            <circle cx="12" cy="12" r="9" />
            <path d="M12 11v5.5M12 7.8h.01" />
          </svg>
        </button>
      </header>

      {videoQuery.isPending || !historyReady ? (
        <div className="grid aspect-video w-full place-items-center bg-ink-900 text-sm text-neutral-500">
          Memuat&hellip;
        </div>
      ) : videoQuery.isError ? (
        <div className="px-4">
          <ErrorMessage
            title="Gagal memuat video"
            message={videoQuery.error.message}
            onRetry={() => void videoQuery.refetch()}
          />
        </div>
      ) : (
        <PlayerBody
          data={videoQuery.data.data}
          dramaTitle={dramaTitle}
          chapters={chapters}
          quality={quality}
          onQuality={setQuality}
          onGoTo={goTo}
          onAllEpisodes={() => navigate(`/drama/${platform}/${id}`)}
          startAt={startAt}
          onProgress={handleProgress}
          rate={rate}
          onRate={setRate}
        />
      )}
    </div>
  );
}

function PlayerBody({
  data,
  dramaTitle,
  chapters,
  quality,
  onQuality,
  onGoTo,
  onAllEpisodes,
  startAt,
  onProgress,
  rate,
  onRate,
}: {
  data: VideoInfo;
  dramaTitle: string;
  chapters: { id: string; index: number; title: string }[];
  quality: string | null;
  onQuality: (q: string | null) => void;
  onGoTo: (chapterId: string, completed?: boolean) => void;
  onAllEpisodes: () => void;
  startAt: number;
  onProgress: (seconds: number, duration: number) => void;
  rate: number;
  onRate: (r: number) => void;
}) {
  // Urutan kualitas: tertinggi dulu, supaya chip terbaca rapi.
  const sorted = [...data.streams].sort(
    (a, b) => score(b.resolution) - score(a.resolution),
  );
  const best = pickBestStream(data.streams);

  // Pilihan yang layak ditampilkan: hanya label bermakna, sudah diseragamkan
  // dan dibuang duplikatnya (rapidtv mengirim "auto" empat kali).
  const seen = new Set<string>();
  const options = sorted
    .filter((s) => isRealQuality(s.resolution))
    .map((s) => ({ stream: s, label: qualityLabel(s.resolution) }))
    .filter((o) => {
      if (seen.has(o.label)) return false;
      seen.add(o.label);
      return true;
    });

  // Satu pilihan saja sama artinya dengan "Auto" — jangan tampilkan dua
  // tombol yang hasilnya identik.
  const showQuality = options.length >= 2;

  const stream =
    (quality !== null
      ? options.find((o) => o.label === quality)?.stream
      : undefined) ?? best;

  if (stream === null || stream === undefined) {
    return (
      <div className="grid aspect-video w-full place-items-center bg-ink-900 text-sm text-neutral-500">
        Tidak ada stream tersedia untuk episode ini.
      </div>
    );
  }

  const hasNext = data.nextChapterId.length > 0 && data.nextChapterId !== '0';
  const hasPrev = data.prevChapterId.length > 0 && data.prevChapterId !== '0';

  // Strip episode: 2 sebelum + aktif + 2 sesudah.
  const pos = chapters.findIndex((c) => c.index === data.episodeIndex);
  const from = pos >= 0 ? Math.max(0, pos - 2) : 0;
  const strip = chapters.slice(from, from + 5);

  return (
    <>
      <VideoPlayer
        stream={stream}
        subtitles={data.subtitles}
        startAt={startAt}
        onProgress={onProgress}
        playbackRate={rate}
        {...(data.drm ? { drm: data.drm } : {})}
        {...(hasNext
          ? { onEnded: () => onGoTo(data.nextChapterId, true) }
          : {})}
      />

      <div className="mx-auto mb-8 max-w-3xl px-4 pt-4 safe-bottom">
        {/* Judul */}
        <h1 className="font-display text-[17px] font-bold">
          {dramaTitle || data.title || `Episode ${data.episodeIndex}`}
        </h1>
        <p className="mt-1 text-[12.5px] text-neutral-400">
          Episode {data.episodeIndex}
          {/* Judul episode sering hanya "Episode N" — jangan diulang. */}
          {data.title &&
          data.title.trim() !== `Episode ${data.episodeIndex}` &&
          data.title.trim() !== dramaTitle.trim()
            ? ` — ${data.title}`
            : ''}
          {/* Beberapa platform mengirim durasi 0 — jangan tampilkan "0 menit". */}
          {data.durationSeconds !== null && data.durationSeconds >= 60
            ? ` · ${Math.round(data.durationSeconds / 60)} menit`
            : ''}
        </p>

        {/* Kualitas — hanya bila memang ada pilihan berbeda. */}
        <section className="mt-5">
          {showQuality && (
            <>
              <h2 className="text-[12.5px] font-bold">Kualitas</h2>
              <div className="mt-2.5 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onQuality(null)}
                  className={`min-h-[44px] flex-1 rounded-xl px-3 text-xs font-bold transition ${
                    quality === null
                      ? 'bg-gold-grad text-gold-ink'
                      : 'bg-ink-800 text-neutral-300'
                  }`}
                >
                  Auto
                </button>
                {options.map((o) => (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => onQuality(o.label)}
                    className={`min-h-[44px] flex-1 rounded-xl px-3 text-xs font-bold transition ${
                      quality === o.label
                        ? 'bg-gold-grad text-gold-ink'
                        : 'bg-ink-800 text-neutral-300'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </>
          )}
          <p className={`text-[11px] text-neutral-600 ${showQuality ? 'mt-2' : ''}`}>
            Format {stream.url.includes('.m3u8') ? 'HLS' : 'MP4'}
            {isRealQuality(stream.resolution)
              ? ` · ${qualityLabel(stream.resolution)}`
              : ''}
            {data.subtitles.length > 0
              ? ` · ${data.subtitles.length} subtitle`
              : ' · tanpa subtitle'}
          </p>
        </section>

        {/* Kecepatan pemutaran */}
        <section className="mt-6">
          <div className="flex items-baseline justify-between">
            <h2 className="text-[12.5px] font-bold">Kecepatan</h2>
            {rate !== 1 && (
              <button
                type="button"
                onClick={() => onRate(1)}
                className="text-[11.5px] font-semibold text-brand-500"
              >
                Kembali normal
              </button>
            )}
          </div>
          <div className="mt-2.5 flex gap-2">
            {RATES.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => onRate(r)}
                className={`min-h-[44px] flex-1 rounded-xl text-xs font-bold transition ${
                  rate === r
                    ? 'bg-gold-grad text-gold-ink'
                    : 'bg-ink-800 text-neutral-300'
                }`}
              >
                {r === 1 ? 'Normal' : `${r}×`}
              </button>
            ))}
          </div>
        </section>

        {/* Pilih episode */}
        {strip.length > 0 && (
          <section className="mt-6">
            <div className="flex items-baseline justify-between">
              <h2 className="text-[12.5px] font-bold">Pilih Episode</h2>
              <button
                type="button"
                onClick={onAllEpisodes}
                className="text-[11.5px] font-semibold text-brand-500"
              >
                Lihat Semua
              </button>
            </div>
            <div className="mt-2.5 flex gap-2">
              {strip.map((c) => {
                const active = c.index === data.episodeIndex;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => onGoTo(chapterIdForUrl(c))}
                    className={`min-h-[44px] flex-1 rounded-xl text-[13px] font-bold transition ${
                      active
                        ? 'bg-gold-grad text-gold-ink'
                        : 'bg-ink-800 text-neutral-300'
                    }`}
                  >
                    {c.index}
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {/* Prev / next */}
        <div className="mt-6 flex gap-2">
          <button
            type="button"
            onClick={() => onGoTo(data.prevChapterId)}
            disabled={!hasPrev}
            className="min-h-[48px] flex-1 rounded-xl border border-line/[0.09] bg-ink-900 text-[13px] font-semibold text-neutral-300 disabled:opacity-40"
          >
            ← Sebelumnya
          </button>
          <button
            type="button"
            onClick={() => onGoTo(data.nextChapterId)}
            disabled={!hasNext}
            className="min-h-[48px] flex-1 rounded-xl bg-gold-grad text-[13px] font-bold text-gold-ink shadow-gold disabled:opacity-40 disabled:shadow-none"
          >
            Berikutnya →
          </button>
        </div>
      </div>
    </>
  );
}
